import type { ReactNode } from 'react';

import { Logo } from './Logo';

interface AuthSplitLayoutProps {
  /** Form heading on the right pane. */
  title: string;
  /** Sub-heading under the form title. */
  subtitle: string;
  /** Big brand tagline on the dark left pane (may include a `.brand-text` accent). */
  tagline: ReactNode;
  /** Optional pill on the brand pane (e.g. "Platform" for the admin login). */
  badge?: string;
  /** The form (and any helper text) rendered on the right pane. */
  children: ReactNode;
}

/**
 * Split-screen auth shell — a dark brand pane (gradient mark, tagline, mesaches
 * signature) on the left and the form on the right. Shared by the tenant and
 * admin logins so both close with the landing's look. The brand pane collapses
 * below `lg`, where a compact logo sits above the form instead.
 */
export function AuthSplitLayout({
  title,
  subtitle,
  tagline,
  badge,
  children,
}: AuthSplitLayoutProps): JSX.Element {
  return (
    <div className="flex min-h-screen">
      {/* Brand pane (desktop only) */}
      <div className="relative hidden w-[44%] max-w-2xl flex-col justify-between overflow-hidden bg-ink-deep p-12 text-white lg:flex">
        <div aria-hidden className="auth-glow pointer-events-none absolute inset-0" />
        <div className="relative">
          <Logo size={34} tone="light" />
        </div>
        <div className="relative max-w-md">
          {badge !== undefined ? (
            <span className="mb-5 inline-flex items-center rounded-full border border-white/15 bg-white/5 px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-white/70">
              {badge}
            </span>
          ) : null}
          <p className="font-display text-3xl font-bold leading-tight tracking-tight">{tagline}</p>
        </div>
        <a
          href="https://mesaches.com"
          target="_blank"
          rel="noopener noreferrer"
          title="mesaches.com"
          className="group relative flex items-center gap-2"
        >
          <span className="font-mono text-[10px] uppercase tracking-wider text-white/45">
            Una plataforma de
          </span>
          <span className="font-mono font-semibold text-white transition group-hover:text-brand-cyan">
            mesaches
          </span>
        </a>
      </div>

      {/* Form pane */}
      <div className="flex flex-1 items-center justify-center bg-paper px-4 py-10">
        <div className="w-full max-w-sm">
          <div className="mb-7 lg:hidden">
            <Logo size={30} />
          </div>
          <div className="mb-6">
            <h1 className="text-2xl font-bold tracking-tight text-ink">{title}</h1>
            <p className="mt-1.5 text-sm text-slate-500">{subtitle}</p>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
